import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { DashboardLayout } from '@/components/DashboardLayout';
import { getBackMarketOrders, getBackMarketOrder, getDashboardStats, createInvoice, getInvoicePdfUrl, getInvoices, markInvoiceSent, sendInvoiceToBM, syncInvoiceToErp, resetAllErpErrors } from '@/services/api';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Package, 
  Search, 
  RefreshCw, 
  ChevronLeft, 
  ChevronRight,
  AlertCircle,
  ExternalLink,
  Loader2,
  FileText,
  CheckCircle,
  X,
  Download,
  Send,
  CheckSquare,
  Square,
  Zap,
  Database,
  AlertTriangle,
  Clock
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';

// Back Market order states (numeric codes)
const orderStates = [
  { value: 'all', label: 'Tous les états' },
  { value: 'pending_invoice', label: '⚠️ Expédiées - En attente facture' },
  { value: '1', label: 'Nouveau' },
  { value: '3', label: 'En cours' },
  { value: '8', label: 'Expédié' },
  { value: '9', label: 'Livré' },
  { value: '0', label: 'Annulé' },
];

// Map state code to label
const getStateLabel = (state) => {
  const stateCode = String(state);
  switch (stateCode) {
    case '1': return 'Nouveau';
    case '3': return 'En cours';
    case '8': return 'Expédié';
    case '9': return 'Livré';
    case '0': return 'Annulé';
    default: return `État ${state}`;
  }
};

const getStateBadgeColor = (state) => {
  const stateCode = String(state);
  switch (stateCode) {
    case '1': return 'bg-blue-100 text-blue-800';
    case '3': return 'bg-yellow-100 text-yellow-800';
    case '8': return 'bg-emerald-100 text-emerald-800';
    case '9': return 'bg-green-100 text-green-800';
    case '0': return 'bg-red-100 text-red-800';
    default: return 'bg-slate-100 text-slate-800';
  }
};

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [connected, setConnected] = useState(false);
  const [pennylaneConnected, setPennylaneConnected] = useState(false);
  const [checkingConnection, setCheckingConnection] = useState(true);
  const [page, setPage] = useState(1);
  const [stateFilter, setStateFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [generatingInvoice, setGeneratingInvoice] = useState(null);
  const [invoicedOrders, setInvoicedOrders] = useState(new Set());
  const [sentOrders, setSentOrders] = useState(new Set());
  const [perPage, setPerPage] = useState(10);
  const [searchedOrder, setSearchedOrder] = useState(null);
  const [searching, setSearching] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(null);
  const [invoiceIdMap, setInvoiceIdMap] = useState({});
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [batchProcessing, setBatchProcessing] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0, currentOrder: '', errors: [] });
  const [erpSyncedOrders, setErpSyncedOrders] = useState(new Set());
  const [syncingErp, setSyncingErp] = useState(null);
  const [erpSyncStatus, setErpSyncStatus] = useState({});
  // Multi-IMEI modal state
  const [imeiModalOpen, setImeiModalOpen] = useState(false);
  const [imeiModalOrder, setImeiModalOrder] = useState(null);
  const [imeiInputs, setImeiInputs] = useState([]);
  const [imeiQuantity, setImeiQuantity] = useState(0);

  useEffect(() => {
    checkConnection();
  }, []);

  const checkConnection = async () => {
    try {
      const stats = await getDashboardStats();
      setConnected(stats.backmarket_connected);
      setPennylaneConnected(stats.pennylane_connected);
      // Charger les factures existantes
      const invoices = await getInvoices();
      const invoiced = new Set(invoices.filter(i => i.pennylane_id).map(i => i.order_id));
      const sent = new Set(invoices.filter(i => i.status === 'sent').map(i => i.order_id));
      const idMap = {};
      const erpSynced = new Set();
      const erpStatus = {};
      invoices.forEach(i => { 
        if (i.pennylane_id) idMap[i.order_id] = i.id;
        if (i.erp_synced) erpSynced.add(i.order_id);
        if (i.erp_sync_status) {
          erpStatus[i.order_id] = { status: i.erp_sync_status, message: i.erp_sync_message };
        }
      });
      setInvoicedOrders(invoiced);
      setSentOrders(sent);
      setInvoiceIdMap(idMap);
      setErpSyncedOrders(erpSynced);
      setErpSyncStatus(erpStatus);
    } catch (error) {
      setConnected(false);
    } finally {
      setCheckingConnection(false);
    }
  };

  useEffect(() => {
    const fetchOrders = async () => {
      if (!connected) return;
      
      setLoading(true);
      try {
        // Si filtre "en attente de facture", utiliser le paramètre spécial
        const isPendingInvoice = stateFilter === 'pending_invoice';
        const state = (stateFilter !== 'all' && stateFilter !== 'pending_invoice') ? stateFilter : null;
        
        const data = await getBackMarketOrders(state, page, isPendingInvoice, perPage);
        
        // Handle paginated response
        if (data && typeof data === 'object' && !Array.isArray(data)) {
          setOrders(data.results || []);
          setTotalCount(data.count || 0);
          setHasNextPage(!!data.next);
          setHasPrevPage(!!data.previous);
        } else {
          setOrders(Array.isArray(data) ? data : []);
          setTotalCount(Array.isArray(data) ? data.length : 0);
          setHasNextPage(false);
          setHasPrevPage(false);
        }
      } catch (error) {
        console.error('Failed to fetch orders:', error);
        const message = error.response?.data?.detail || 'Erreur lors du chargement des commandes';
        toast.error(message);
        setOrders([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };

    if (connected) {
      fetchOrders();
    }
  }, [connected, page, stateFilter, perPage]);

  // Manual refresh function
  const handleRefresh = async () => {
    if (!connected) return;
    
    setLoading(true);
    try {
      const isPendingInvoice = stateFilter === 'pending_invoice';
      const state = (stateFilter !== 'all' && stateFilter !== 'pending_invoice') ? stateFilter : null;
      
      const data = await getBackMarketOrders(state, page, isPendingInvoice, perPage);
      
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        setOrders(data.results || []);
        setTotalCount(data.count || 0);
        setHasNextPage(!!data.next);
        setHasPrevPage(!!data.previous);
      } else {
        setOrders(Array.isArray(data) ? data : []);
        setTotalCount(Array.isArray(data) ? data.length : 0);
        setHasNextPage(false);
        setHasPrevPage(false);
      }
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      const message = error.response?.data?.detail || 'Erreur lors du chargement des commandes';
      toast.error(message);
      setOrders([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = orders.filter(order => {
    if (!searchQuery || searchedOrder) return true;
    const query = searchQuery.toLowerCase();
    return (
      order.order_id?.toString().toLowerCase().includes(query) ||
      order.billing_address?.first_name?.toLowerCase().includes(query) ||
      order.billing_address?.last_name?.toLowerCase().includes(query)
    );
  });

  // Recherche par numéro de commande via l'API
  const handleSearch = async () => {
    const query = searchQuery.trim();
    if (!query) {
      setSearchedOrder(null);
      return;
    }
    // Si c'est un numéro (potentiel order_id), chercher via l'API
    if (/^\d{5,}$/.test(query)) {
      setSearching(true);
      try {
        const order = await getBackMarketOrder(query);
        if (order && order.order_id) {
          setSearchedOrder(order);
        } else {
          toast.error(`Commande #${query} non trouvée`);
          setSearchedOrder(null);
        }
      } catch {
        toast.error(`Commande #${query} non trouvée`);
        setSearchedOrder(null);
      } finally {
        setSearching(false);
      }
    } else {
      setSearchedOrder(null);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchedOrder(null);
  };

  // Generate invoice from order
  const handleGenerateInvoice = async (orderId) => {
    setGeneratingInvoice(orderId);
    try {
      // Get full order details
      const orderDetails = await getBackMarketOrder(orderId);
      
      // Vérifier les IMEIs disponibles sur chaque ligne de commande BM
      const totalQuantity = orderDetails.orderlines?.reduce((sum, l) => sum + (l.quantity || 1), 0) || 1;
      const collected = [];
      let missing = false;
      for (const l of (orderDetails.orderlines || [])) {
        const q = l.quantity || 1;
        const li = (l.imei || '').trim();
        if (q === 1 && li) { collected.push(li); }
        else { for (let k = 0; k < q; k++) collected.push(''); missing = true; }
      }

      if (missing) {
        // IMEI(s) manquant(s) sur BM -> saisie manuelle obligatoire (prérempli avec ceux connus)
        setImeiModalOrder(orderDetails);
        setImeiQuantity(totalQuantity);
        setImeiInputs(collected);
        setImeiModalOpen(true);
        setGeneratingInvoice(null);
        return;
      }

      // Tous les IMEIs connus via les lignes BM
      await processInvoice(orderDetails, collected);
    } catch (error) {
      console.error('Failed to generate invoice:', error);
      const message = error.response?.data?.detail || 'Erreur lors de la génération de la facture';
      toast.error(message);
      setGeneratingInvoice(null);
    }
  };

  // Traiter la facturation (appelé directement ou après saisie IMEI)
  const processInvoice = async (orderDetails, imeis) => {
    const orderId = orderDetails.order_id;
    setGeneratingInvoice(orderId);
    try {
      const firstLine = orderDetails.orderlines?.[0];
      const sku = firstLine?.listing || '';
      
      const items = orderDetails.orderlines?.map(line => ({
        label: line.product || 'Article',
        quantity: line.quantity || 1,
        price: parseFloat(line.price) || 0,
        imei: (line.imei || '').trim(),
        sku: line.listing || ''
      })) || [];

      const totalAmount = items.reduce((sum, item) => sum + (item.quantity * item.price), 0);
      const ba = orderDetails.billing_address || {};

      const invoiceData = {
        order_id: String(orderId),
        customer_name: `${ba.first_name || ''} ${ba.last_name || ''}`.trim(),
        customer_email: ba.email || '',
        customer_address: `${ba.street || ''}, ${ba.postal_code || ''} ${ba.city || ''}`.trim(),
        items: items,
        total_amount: totalAmount,
        currency: orderDetails.currency || 'EUR',
        billing_first_name: ba.first_name || '',
        billing_last_name: ba.last_name || '',
        billing_street: ba.street || '',
        billing_street2: ba.street2 || '',
        billing_postal_code: ba.postal_code || '',
        billing_city: ba.city || '',
        billing_country: ba.country || 'FR',
        sku: sku,
        imei: firstLine?.imei || '',
        imeis: imeis || []
      };

      const result = await createInvoice(invoiceData);
      
      if (result.status === 'finalized') {
        setInvoicedOrders(prev => new Set([...prev, String(orderId)]));
        setInvoiceIdMap(prev => ({ ...prev, [String(orderId)]: result.id }));
        if (result.pdf_url) {
          toast.success(`Facture finalisée pour #${orderId} - Ouverture du PDF...`);
          window.open(result.pdf_url, '_blank');
        } else {
          toast.success(`Facture finalisée pour #${orderId} - Le PDF sera disponible dans quelques instants via le bouton PDF`);
        }
      } else if (result.status === 'created') {
        toast.warning(`Facture créée pour #${orderId} mais pas encore finalisée. Réessayez le bouton PDF.`);
        setInvoicedOrders(prev => new Set([...prev, String(orderId)]));
        setInvoiceIdMap(prev => ({ ...prev, [String(orderId)]: result.id }));
      } else if (result.status === 'error') {
        toast.error('Erreur Pennylane - Facture sauvegardée localement');
      } else {
        toast.success(`Facture créée (${result.status}) pour #${orderId}`);
        setInvoicedOrders(prev => new Set([...prev, String(orderId)]));
      }
    } catch (error) {
      console.error('Failed to generate invoice:', error);
      const message = error.response?.data?.detail || 'Erreur lors de la génération de la facture';
      toast.error(message);
    } finally {
      setGeneratingInvoice(null);
    }
  };

  // Confirmer les IMEIs et lancer la facturation
  const handleImeiConfirm = async () => {
    const validImeis = imeiInputs.filter(i => i.trim() !== '');
    if (validImeis.length !== imeiQuantity) {
      toast.error(`Veuillez saisir les ${imeiQuantity} IMEIs`);
      return;
    }
    // Vérifier le format IMEI (15 chiffres)
    const invalidImeis = validImeis.filter(i => !/^\d{15}$/.test(i.trim()));
    if (invalidImeis.length > 0) {
      toast.error('Format IMEI invalide. Un IMEI doit contenir exactement 15 chiffres.');
      return;
    }
    setImeiModalOpen(false);
    await processInvoice(imeiModalOrder, validImeis.map(i => i.trim()));
    setImeiModalOrder(null);
    setImeiInputs([]);
  };

  // Determine which orders can be selected (not already invoiced/sent)
  const getSelectableOrders = () => {
    const displayedOrders = searchedOrder ? [searchedOrder] : filteredOrders;
    return displayedOrders.filter(o => {
      const oid = String(o.order_id);
      return !sentOrders.has(oid) && !invoicedOrders.has(oid);
    });
  };

  const toggleSelectOrder = (orderId) => {
    const oid = String(orderId);
    setSelectedOrders(prev => {
      const next = new Set(prev);
      if (next.has(oid)) next.delete(oid);
      else next.add(oid);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const selectable = getSelectableOrders();
    const allSelected = selectable.length > 0 && selectable.every(o => selectedOrders.has(String(o.order_id)));
    if (allSelected) {
      setSelectedOrders(new Set());
    } else {
      setSelectedOrders(new Set(selectable.map(o => String(o.order_id))));
    }
  };

  // Batch: Facturer + Envoyer pour toutes les commandes sélectionnées
  const handleBatchInvoiceAndSend = async () => {
    const orderIds = Array.from(selectedOrders);
    if (orderIds.length === 0) return;

    setBatchProcessing(true);
    setBatchProgress({ current: 0, total: orderIds.length, currentOrder: '', errors: [] });
    const errors = [];

    for (let i = 0; i < orderIds.length; i++) {
      const orderId = orderIds[i];
      setBatchProgress(prev => ({ ...prev, current: i, currentOrder: orderId }));

      try {
        // 1. Récupérer les détails de la commande
        const orderDetails = await getBackMarketOrder(orderId);
        const firstLine = orderDetails.orderlines?.[0];
        const sku = firstLine?.listing || '';
        const totalQuantity = orderDetails.orderlines?.reduce((sum, l) => sum + (l.quantity || 1), 0) || 1;
        // Collecter les IMEIs par ligne BM
        const collected = [];
        let missing = false;
        for (const l of (orderDetails.orderlines || [])) {
          const q = l.quantity || 1;
          const li = (l.imei || '').trim();
          if (q === 1 && li) { collected.push(li); }
          else { for (let k = 0; k < q; k++) collected.push(''); missing = true; }
        }

        // Skip orders with missing IMEI in batch mode (sinon facture sans IMEI -> ERP bloqué)
        if (missing) {
          errors.push({ orderId, step: 'imei', message: `IMEI manquant sur Back Market : facturez cette commande individuellement (saisie manuelle)` });
          continue;
        }
        
        const items = orderDetails.orderlines?.map(line => ({
          label: line.product || 'Article',
          quantity: line.quantity || 1,
          price: parseFloat(line.price) || 0,
          imei: (line.imei || '').trim(),
          sku: line.listing || ''
        })) || [];
        const totalAmount = items.reduce((sum, item) => sum + (item.quantity * item.price), 0);
        const ba = orderDetails.billing_address || {};

        // 2. Créer et finaliser la facture
        const result = await createInvoice({
          order_id: orderId,
          customer_name: `${ba.first_name || ''} ${ba.last_name || ''}`.trim(),
          customer_email: ba.email || '',
          customer_address: `${ba.street || ''}, ${ba.postal_code || ''} ${ba.city || ''}`.trim(),
          items, total_amount: totalAmount, currency: orderDetails.currency || 'EUR',
          billing_first_name: ba.first_name || '', billing_last_name: ba.last_name || '',
          billing_street: ba.street || '', billing_street2: ba.street2 || '',
          billing_postal_code: ba.postal_code || '', billing_city: ba.city || '',
          billing_country: ba.country || 'FR', sku, imei: firstLine?.imei || '', imeis: collected
        });

        setInvoicedOrders(prev => new Set([...prev, orderId]));
        setInvoiceIdMap(prev => ({ ...prev, [orderId]: result.id }));

        // 3. Envoyer par email si la facture est finalisée/créée
        if (result.id && (result.status === 'finalized' || result.status === 'created')) {
          try {
            await sendInvoiceToBM(orderId, result.id);
            setSentOrders(prev => new Set([...prev, orderId]));
            setErpSyncedOrders(prev => new Set([...prev, orderId]));
          } catch (emailErr) {
            errors.push({ orderId, step: 'email', message: emailErr.response?.data?.detail || 'Erreur envoi email' });
          }
        } else {
          errors.push({ orderId, step: 'invoice', message: `Status: ${result.status}` });
        }
      } catch (err) {
        errors.push({ orderId, step: 'invoice', message: err.response?.data?.detail || err.message });
      }

      // Petite pause entre chaque pour ne pas surcharger les APIs
      await new Promise(r => setTimeout(r, 500));
    }

    setBatchProgress({ current: orderIds.length, total: orderIds.length, currentOrder: '', errors });
    setSelectedOrders(new Set());

    if (errors.length === 0) {
      toast.success(`${orderIds.length} factures créées et envoyées avec succès !`);
    } else {
      toast.warning(`${orderIds.length - errors.length}/${orderIds.length} factures traitées. ${errors.length} erreur(s).`);
    }

    setBatchProcessing(false);
  };

  if (checkingConnection) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      </DashboardLayout>
    );
  }

  if (!connected) {
    return (
      <DashboardLayout>
        <div className="space-y-6" data-testid="orders-page">
          <div>
            <h1 
              className="text-3xl font-bold text-slate-900"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Commandes
            </h1>
            <p className="text-slate-600 mt-1">
              Gérez vos commandes Back Market
            </p>
          </div>

          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0" />
                <div>
                  <h3 className="font-medium text-amber-900">Back Market non connecté</h3>
                  <p className="text-sm text-amber-700 mt-1">
                    Veuillez configurer votre token API Back Market dans les paramètres pour accéder à vos commandes.
                  </p>
                  <Button 
                    variant="outline" 
                    className="mt-4 border-amber-300 text-amber-700 hover:bg-amber-100"
                    onClick={() => window.location.href = '/settings'}
                    data-testid="go-to-settings-btn"
                  >
                    Configurer le token
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="orders-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 
              className="text-3xl font-bold text-slate-900"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Commandes
            </h1>
            <p className="text-slate-600 mt-1">
              Gérez vos commandes Back Market
            </p>
          </div>
          <Button 
            onClick={handleRefresh} 
            disabled={loading}
            className="bg-emerald-500 hover:bg-emerald-600"
            data-testid="refresh-orders-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>

        {/* Filters */}
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Rechercher par numéro de commande..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      if (!e.target.value) clearSearch();
                    }}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                    className="pl-10 pr-20"
                    data-testid="search-orders-input"
                  />
                  {searchQuery && (
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-slate-400 hover:text-slate-600"
                        onClick={clearSearch}
                        data-testid="clear-search-btn"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        className="h-7 px-3 bg-emerald-500 hover:bg-emerald-600 text-white"
                        onClick={handleSearch}
                        disabled={searching}
                        data-testid="search-btn"
                      >
                        {searching ? <RefreshCw className="w-3 h-3 animate-spin" /> : 'OK'}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              <Select value={stateFilter} onValueChange={(value) => {
                setStateFilter(value);
                setPage(1); // Reset to page 1 when filter changes
              }}>
                <SelectTrigger className="w-full md:w-48" data-testid="filter-state-select">
                  <SelectValue placeholder="Filtrer par état" />
                </SelectTrigger>
                <SelectContent>
                  {orderStates.map((state) => (
                    <SelectItem key={state.value} value={state.value}>
                      {state.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={String(perPage)} onValueChange={(value) => {
                setPerPage(Number(value));
                setPage(1);
              }}>
                <SelectTrigger className="w-full md:w-32" data-testid="per-page-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10 / page</SelectItem>
                  <SelectItem value="20">20 / page</SelectItem>
                  <SelectItem value="50">50 / page</SelectItem>
                  <SelectItem value="100">100 / page</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Orders List */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Liste des commandes
            </CardTitle>
            <CardDescription>
              {searchedOrder ? (
                <>
                  Résultat pour la commande <span className="font-semibold text-emerald-600">#{searchedOrder.order_id}</span>
                </>
              ) : stateFilter === 'all' && !searchQuery ? (
                <>
                  <span className="font-semibold text-emerald-600">{totalCount.toLocaleString('fr-FR')}</span> commande(s) au total
                  {' • '}Page {page} ({filteredOrders.length} affichée(s))
                </>
              ) : stateFilter === 'pending_invoice' ? (
                <>
                  <span className="font-semibold text-orange-600">{totalCount}</span> commande(s) en attente de facture
                  {' • '}Page {page} ({filteredOrders.length} affichée(s))
                </>
              ) : (
                <>
                  <span className="font-semibold text-blue-600">{totalCount}</span> commande(s) {orderStates.find(s => s.value === stateFilter)?.label || ''}
                  {' • '}Page {page} ({filteredOrders.length} affichée(s))
                </>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Batch action bar */}
            {(() => {
              const selectable = getSelectableOrders();
              const allSelected = selectable.length > 0 && selectable.every(o => selectedOrders.has(String(o.order_id)));
              const someSelected = selectedOrders.size > 0;
              return (selectable.length > 0 && pennylaneConnected && !batchProcessing) ? (
                <div className="flex items-center gap-3 mb-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={toggleSelectAll}
                    data-testid="select-all-checkbox"
                  />
                  <span className="text-sm text-slate-600">
                    {someSelected ? `${selectedOrders.size} sélectionnée(s)` : 'Tout sélectionner'}
                  </span>
                  {someSelected && (
                    <Button
                      size="sm"
                      onClick={handleBatchInvoiceAndSend}
                      className="ml-auto bg-emerald-500 hover:bg-emerald-600 text-white"
                      data-testid="batch-invoice-btn"
                    >
                      <Zap className="w-4 h-4 mr-1" />
                      Facturer + Envoyer ({selectedOrders.size})
                    </Button>
                  )}
                </div>
              ) : null;
            })()}

            {/* Batch progress */}
            {batchProcessing && (
              <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200" data-testid="batch-progress">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-blue-800">
                    Traitement en cours... {batchProgress.current}/{batchProgress.total}
                  </span>
                  <span className="text-xs text-blue-600">
                    Commande #{batchProgress.currentOrder}
                  </span>
                </div>
                <Progress value={(batchProgress.current / batchProgress.total) * 100} className="h-2" />
                {batchProgress.errors.length > 0 && (
                  <div className="mt-2">
                    {batchProgress.errors.map((e, i) => (
                      <p key={i} className="text-xs text-red-600">#{e.orderId}: {e.message}</p>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ERP bulk reset banner */}
            {(() => {
              const erpErrorCount = Object.values(erpSyncStatus).filter(s => s.status === 'error' || s.status === 'pending').length;
              if (erpErrorCount === 0) return null;
              return (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between" data-testid="erp-bulk-reset-banner">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                    <span className="text-sm text-amber-800">{erpErrorCount} facture{erpErrorCount > 1 ? 's' : ''} avec statut ERP incorrect</span>
                  </div>
                  <Button size="sm" variant="outline" className="text-xs border-amber-300 text-amber-700 hover:bg-amber-100"
                    data-testid="erp-bulk-reset-btn"
                    onClick={async () => {
                      try {
                        const res = await resetAllErpErrors();
                        setErpSyncStatus({});
                        const allIds = Object.keys(erpSyncStatus);
                        setErpSyncedOrders(prev => {
                          const next = new Set(prev);
                          allIds.forEach(id => next.add(id));
                          return next;
                        });
                        toast.success(`${res.corrected} factures ERP corrigees`);
                      } catch { toast.error('Erreur'); }
                    }}>
                    Tout corriger ERP
                  </Button>
                </div>
              );
            })()}

            {loading || searching ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
              </div>
            ) : (searchedOrder ? [searchedOrder] : filteredOrders).length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Aucune commande trouvée</p>
              </div>
            ) : (
              <div className="space-y-3">
                {(searchedOrder ? [searchedOrder] : filteredOrders).map((order, index) => (
                  <motion.div
                    key={order.order_id || index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: index * 0.05 }}
                  >
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                      <div className="flex items-center gap-4">
                        {/* Checkbox - only for non-invoiced/non-sent orders */}
                        {!sentOrders.has(String(order.order_id)) && !invoicedOrders.has(String(order.order_id)) && pennylaneConnected ? (
                          <Checkbox
                            checked={selectedOrders.has(String(order.order_id))}
                            onCheckedChange={() => toggleSelectOrder(order.order_id)}
                            data-testid={`select-order-${order.order_id}`}
                            disabled={batchProcessing}
                          />
                        ) : (
                          <div className="w-4" />
                        )}
                        <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                          <Package className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">
                            Commande #{order.order_id}
                            {(() => {
                              const qty = order.orderlines?.reduce((sum, l) => sum + (l.quantity || 1), 0) || 1;
                              return qty > 1 ? (
                                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700" data-testid={`qty-badge-${order.order_id}`}>
                                  x{qty}
                                </span>
                              ) : null;
                            })()}
                          </p>
                          <p className="text-sm text-slate-500">
                            {order.billing_address?.first_name} {order.billing_address?.last_name}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right mr-2">
                          <p className="font-medium text-slate-900">
                            {order.price ? `${parseFloat(order.price).toFixed(2)} €` : 'N/A'}
                          </p>
                          <p className="text-xs text-slate-500">
                            {order.date_creation ? new Date(order.date_creation).toLocaleDateString('fr-FR') : ''}
                          </p>
                        </div>
                        <Badge className={getStateBadgeColor(order.state)}>
                          {getStateLabel(order.state)}
                        </Badge>
                        {/* Actions facture */}
                        {sentOrders.has(String(order.order_id)) ? (
                          <div className="flex items-center gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-orange-300 text-orange-600 hover:bg-orange-50"
                              data-testid={`pdf-btn-${order.order_id}`}
                              onClick={async () => {
                                try {
                                  const data = await getInvoicePdfUrl(String(order.order_id));
                                  window.open(data.pdf_url, '_blank');
                                } catch {
                                  toast.error('PDF non disponible');
                                }
                              }}
                            >
                              <Download className="w-4 h-4 mr-1" />
                              PDF
                            </Button>
                            <div className="flex items-center gap-1 px-2 py-1 bg-green-50 border border-green-200 rounded-md" data-testid={`sent-badge-${order.order_id}`}>
                              <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                              <span className="text-xs font-medium text-green-700">Envoyée</span>
                            </div>
                            {erpSyncStatus[String(order.order_id)]?.status === 'error' ? (
                              <button
                                className="flex items-center gap-1 px-2 py-1 bg-red-50 border border-red-200 rounded-md cursor-pointer hover:bg-red-100 transition-colors"
                                data-testid={`erp-error-${order.order_id}`}
                                title={`${erpSyncStatus[String(order.order_id)]?.message || 'Erreur ERP'} — Cliquez pour marquer OK`}
                                onClick={async () => {
                                  const oid = String(order.order_id);
                                  try {
                                    const token = localStorage.getItem('token');
                                    const apiBase = process.env.NODE_ENV === 'production' ? '' : process.env.REACT_APP_BACKEND_URL;
                                    await axios.post(
                                      `${apiBase}/api/invoices/${oid}/reset-erp`,
                                      {},
                                      { headers: { Authorization: `Bearer ${token}` } }
                                    );
                                    setErpSyncedOrders(prev => new Set([...prev, oid]));
                                    setErpSyncStatus(prev => { const n = {...prev}; delete n[oid]; return n; });
                                    toast.success(`ERP #${oid} corrigé`);
                                  } catch {
                                    toast.error('Erreur');
                                  }
                                }}
                              >
                                <AlertTriangle className="w-3.5 h-3.5 text-red-600" />
                                <span className="text-xs font-medium text-red-700">ERP</span>
                              </button>
                            ) : erpSyncStatus[String(order.order_id)]?.status === 'pending' ? (
                              <div className="flex items-center gap-1 px-2 py-1 bg-amber-50 border border-amber-200 rounded-md" data-testid={`erp-pending-${order.order_id}`}
                                title="En attente de confirmation ERP">
                                <Clock className="w-3.5 h-3.5 text-amber-600" />
                                <span className="text-xs font-medium text-amber-700">ERP</span>
                              </div>
                            ) : erpSyncedOrders.has(String(order.order_id)) ? (
                              <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 border border-blue-200 rounded-md" data-testid={`erp-badge-${order.order_id}`}>
                                <CheckCircle className="w-3.5 h-3.5 text-blue-600" />
                                <span className="text-xs font-medium text-blue-700">ERP</span>
                              </div>
                            ) : null}
                          </div>
                        ) : invoicedOrders.has(String(order.order_id)) ? (
                          <div className="flex gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-orange-300 text-orange-600 hover:bg-orange-50"
                              data-testid={`pdf-btn-${order.order_id}`}
                              onClick={async () => {
                                try {
                                  const data = await getInvoicePdfUrl(String(order.order_id));
                                  window.open(data.pdf_url, '_blank');
                                } catch {
                                  toast.error('PDF non disponible');
                                }
                              }}
                            >
                              <Download className="w-4 h-4 mr-1" />
                              PDF
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-emerald-300 text-emerald-600 hover:bg-emerald-50"
                              data-testid={`send-bm-${order.order_id}`}
                              disabled={sendingEmail === order.order_id}
                              onClick={async () => {
                                const oid = String(order.order_id);
                                const invoiceId = invoiceIdMap[oid];
                                if (!invoiceId) {
                                  toast.error('ID facture introuvable');
                                  return;
                                }
                                setSendingEmail(order.order_id);
                                try {
                                  const res = await sendInvoiceToBM(oid, invoiceId);
                                  setSentOrders(prev => new Set([...prev, oid]));
                                  setErpSyncedOrders(prev => new Set([...prev, oid]));
                                  toast.success(`Facture envoyée à ${res.email || 'Back Market'}`);
                                } catch (err) {
                                  const msg = err.response?.data?.detail || 'Erreur envoi email';
                                  toast.error(msg);
                                } finally {
                                  setSendingEmail(null);
                                }
                              }}
                            >
                              {sendingEmail === order.order_id ? (
                                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                              ) : (
                                <Send className="w-4 h-4 mr-1" />
                              )}
                              Envoyer BM
                            </Button>
                          </div>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleGenerateInvoice(order.order_id)}
                            disabled={generatingInvoice === order.order_id || !pennylaneConnected}
                            className="border-blue-300 text-blue-700 hover:bg-blue-50"
                            data-testid={`invoice-btn-${order.order_id}`}
                            title={!pennylaneConnected ? "Configurez Pennylane dans les paramètres" : "Générer la facture"}
                          >
                            {generatingInvoice === order.order_id ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                Création...
                              </>
                            ) : (
                              <>
                                <FileText className="w-4 h-4 mr-1" />
                                Facturer
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            {/* Pagination - hidden during search */}
            {!searchedOrder && filteredOrders.length > 0 && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-200">
                <Button
                  variant="outline"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={!hasPrevPage || loading}
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Précédent
                </Button>
                <div className="text-center">
                  <span className="text-sm font-medium text-slate-900">Page {page}</span>
                  <span className="text-xs text-slate-500 block">
                    sur ~{Math.ceil(totalCount / perPage)} pages
                  </span>
                </div>
                <Button
                  variant="outline"
                  onClick={() => setPage(p => p + 1)}
                  disabled={!hasNextPage || loading}
                  data-testid="next-page-btn"
                >
                  Suivant
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Modal de saisie des IMEIs pour commandes multi-articles */}
      <Dialog open={imeiModalOpen} onOpenChange={(open) => {
        if (!open) {
          setImeiModalOpen(false);
          setImeiModalOrder(null);
          setImeiInputs([]);
        }
      }}>
        <DialogContent className="sm:max-w-md" data-testid="imei-modal">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Saisie {imeiQuantity > 1 ? 'des IMEIs' : "de l'IMEI"}
            </DialogTitle>
            <DialogDescription>
              La commande <span className="font-semibold">#{imeiModalOrder?.order_id}</span> contient{' '}
              <span className="font-semibold">{imeiQuantity} article{imeiQuantity > 1 ? 's' : ''}</span> et il manque un IMEI.
              <br />
              Saisissez l'IMEI de chaque appareil (15 chiffres) — la facture ne peut pas être générée sans IMEI.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {imeiInputs.map((val, idx) => (
              <div key={idx} className="space-y-1">
                <Label htmlFor={`imei-${idx}`} className="text-sm text-slate-600">
                  Appareil {idx + 1}/{imeiQuantity}
                </Label>
                <Input
                  id={`imei-${idx}`}
                  placeholder="Ex: 353346541286084"
                  value={val}
                  onChange={(e) => {
                    const next = [...imeiInputs];
                    next[idx] = e.target.value.replace(/\D/g, '').slice(0, 15);
                    setImeiInputs(next);
                  }}
                  maxLength={15}
                  className={val.length === 15 ? 'border-emerald-400' : ''}
                  data-testid={`imei-input-${idx}`}
                />
                {val.length > 0 && val.length < 15 && (
                  <p className="text-xs text-amber-600">{15 - val.length} chiffre(s) restant(s)</p>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setImeiModalOpen(false);
                setImeiModalOrder(null);
                setImeiInputs([]);
              }}
              data-testid="imei-cancel-btn"
            >
              Annuler
            </Button>
            <Button
              onClick={handleImeiConfirm}
              disabled={imeiInputs.some(i => i.trim().length !== 15)}
              className="bg-emerald-500 hover:bg-emerald-600"
              data-testid="imei-confirm-btn"
            >
              <FileText className="w-4 h-4 mr-1" />
              Facturer avec IMEIs
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
